/**
 * incomingValueToken.js
 *
 * The editor-facing alias for a binding's incoming value inside a
 * composed string result: `{{value}}`.
 *
 * A mapping result is emitted verbatim into the generated template, so the
 * only thing that actually works at runtime is the binding's real Jinja
 * expression — `{{ states("sensor.x") }}`, or whatever
 * buildIncomingValueExpression() derives once transforms and attributes
 * are taken into account. That expression is therefore what `rule.result`
 * stores, and it is the single source of truth: nothing here builds a
 * second entity expression.
 *
 * It is also unreadable in an 8rem-wide result field. So the editor shows
 * `{{value}}` instead and converts in both directions at the field
 * boundary — exactly the pattern utils/iconTextNotation.js already
 * establishes for icons in the same input (stored form = the real thing,
 * displayed form = the readable thing). The model, generator, YAML
 * serialization and preview never see the alias.
 *
 * The conversion needs the binding's expression, so it is a no-op while a
 * binding has no entity selected — there is nothing to alias yet. A result
 * that ends up holding a literal alias in that window is a real defect
 * (Jinja would render it as nothing), which is why validateMappingRules.js
 * has an `unresolved-incoming-value` check for it rather than this module
 * silently guessing.
 *
 * Pure string functions — no Vue, no generator.
 */

/** What the designer sees and types; never stored, never generated. */
export const INCOMING_VALUE_TOKEN = '{{value}}';

/** @private — `{{value}}`, tolerating inner whitespace. */
const TOKEN_PATTERN = /\{\{\s*value\s*\}\}/g;

/**
 * A Jinja output tag wrapping one exact expression, tolerating inner
 * whitespace: `{{ states("x") }}` and `{{states("x")}}` both match.
 * The single definition of "this result refers to the incoming value" —
 * shared with previewMappingResult.js so the editor and the preview can
 * never disagree about what counts as a reference.
 *
 * @param {string} expression
 * @returns {RegExp}
 */
export function jinjaExpressionPattern(expression) {
  const escaped = expression.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`\\{\\{\\s*${escaped}\\s*\\}\\}`, 'g');
}

/**
 * Stored form → editable form: the binding's own value expression becomes
 * `{{value}}`. Any other Jinja is left exactly as written.
 *
 * @param {*} text
 * @param {string} [expression] — from buildIncomingValueExpression()
 * @returns {string}
 */
export function toIncomingValueToken(text, expression = '') {
  const out = text === undefined || text === null ? '' : String(text);
  if (!expression) return out;
  return out.replace(jinjaExpressionPattern(expression), INCOMING_VALUE_TOKEN);
}

/**
 * Editable form → stored form: `{{value}}` becomes the binding's real
 * value expression, so what reaches `rule.result` is runnable Jinja.
 *
 * @param {*} text
 * @param {string} [expression] — from buildIncomingValueExpression()
 * @returns {string}
 */
export function fromIncomingValueToken(text, expression = '') {
  const out = text === undefined || text === null ? '' : String(text);
  if (!expression) return out;
  return out.replace(TOKEN_PATTERN, `{{ ${expression} }}`);
}

/**
 * Does this text still carry the editor alias? True only for a value that
 * never got bound to an expression — see the module comment.
 *
 * @param {*} text
 * @returns {boolean}
 */
export function hasIncomingValueToken(text) {
  if (text === undefined || text === null) return false;
  return new RegExp(TOKEN_PATTERN.source).test(String(text));
}
